import React from "react";
import { message } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_USER_ORDER_MODERATION,
  SET_USER_ORDER_MODERATION,
} from "../../api/graphql/moderation";
import ModerationSwitch from "./ModerationSwitch";
import styles from "./styles.module.scss";

type Mode = "AUTO" | "MANUAL";

/**
 * Manual moderation of the user's orders, switched right in place: the
 * switch of the ASA platform's tables (Campaigns). On, orders wait for an
 * admin on the Moderation page; off, they go to the contractor at once.
 */
const UserModerationToggle: React.FC<{ userId: number }> = ({ userId }) => {
  const { data, error, refetch } = useQuery<{ getUserOrderModeration: Mode }>(
    GET_USER_ORDER_MODERATION,
    { variables: { userId }, fetchPolicy: "network-only" },
  );
  const [setMode, { loading }] = useMutation(SET_USER_ORDER_MODERATION);
  const mode = data?.getUserOrderModeration;
  const manual = mode === "MANUAL";

  // The backend has no moderation yet: nothing to switch.
  if (error) return <span className={styles.toggleNote}>—</span>;

  const handleToggle = async () => {
    const next: Mode = manual ? "AUTO" : "MANUAL";
    try {
      await setMode({ variables: { userId, mode: next } });
      await refetch();
      message.success(
        next === "MANUAL"
          ? "New orders wait for an admin on the Moderation page"
          : "Orders go to the contractor at once; waiting ones are sent now",
      );
    } catch (e: any) {
      message.error(e?.message || "The moderation mode was not changed");
    }
  };

  return (
    <span className={styles.toggleRow}>
      <ModerationSwitch
        checked={manual}
        disabled={!mode || loading}
        ariaLabel="Manual moderation of orders"
        onToggle={handleToggle}
      />
      <span className={styles.toggleNote}>
        {!mode ? "…" : manual ? "Waits for an admin" : "Sent at once"}
      </span>
    </span>
  );
};

export default UserModerationToggle;
